import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, FileText, ExternalLink, Package, Ruler } from "lucide-react";

const AmazonProductsDatasheets = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState("products");

  // Placeholder products - these would come from API with actual dimensions
  const products = [
    { 
      name: 'Comet Speedlane', 
      category: 'Speedlanes', 
      description: 'High-speed optical turnstile',
      width: 'TBC mm',
      image: null
    },
    { 
      name: 'Access Gate', 
      category: 'Gates', 
      description: 'Swing gate for accessible entry',
      width: 'TBC mm',
      image: null
    },
  ];

  // Placeholder datasheets - these would come from API in production
  const datasheets = [
    { name: 'Comet Speedlane', category: 'Speedlanes', url: '#' },
    { name: 'Access Gates', category: 'Gates', url: '#' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-100 p-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={onBack}
          className="mb-6 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
              <Package className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Products and Datasheets</h1>
          <p className="text-slate-600">Browse products and documentation</p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="products" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Products
            </TabsTrigger>
            <TabsTrigger value="datasheets" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Datasheets
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="grid gap-4 md:grid-cols-2">
              {products.map((product, index) => (
                <Card key={index} className="hover:border-blue-300 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Package className="h-8 w-8 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{product.name}</h3>
                        <p className="text-sm text-slate-500 mb-2">{product.category}</p>
                        <p className="text-sm text-slate-600">{product.description}</p>
                        <div className="flex items-center gap-1 mt-2 text-sm text-orange-600">
                          <Ruler className="h-4 w-4" />
                          <span>Width: {product.width}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Datasheets Tab */}
          <TabsContent value="datasheets">
            <div className="grid gap-4">
              {datasheets.map((sheet, index) => (
                <Card key={index} className="hover:border-blue-300 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-medium">{sheet.name}</h3>
                          <p className="text-sm text-slate-500">{sheet.category}</p>
                        </div>
                      </div>
                      <Button variant="outline" size="sm">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AmazonProductsDatasheets;
